import { supabase } from "../client";

// ── Users ────────────────────────────────────────────────────

export async function fetchUserByEmail(email) {
  const { data, error } = await supabase
    .from("users")
    .select("role")
    .eq("email", email)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function upsertUser(fields) {
  const { data, error } = await supabase
    .from("users")
    .upsert(fields, { onConflict: "id" })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function fetchUsersByIds(ids) {
  if (!ids.length) return [];
  const { data, error } = await supabase
    .from("users")
    .select("id, full_name, email")
    .in("id", ids);
  if (error) throw error;
  return data || [];
}

export async function fetchActiveUsers() {
  const { data, error } = await supabase
    .from("users")
    .select("id, full_name, email, role")
    .eq("is_active", true)
    .order("full_name");
  if (error) throw error;
  return data || [];
}

export async function updateLastActive(userId) {
  const { error } = await supabase
    .from("users")
    .update({ last_active: new Date().toISOString() })
    .eq("id", userId);
  if (error) throw error;
}

// ── Login Logs ───────────────────────────────────────────────

export async function insertLoginLog(userId, email, ip, userAgent) {
  const { error } = await supabase
    .from("login_logs")
    .insert({ user_id: userId, email, ip_address: ip, user_agent: userAgent });
  if (error) throw error;
}

export async function purgeOldLoginLogs(cutoffDate) {
  const { error } = await supabase
    .from("login_logs")
    .delete()
    .lt("logged_in_at", cutoffDate);
  if (error) throw error;
}

// ── Section Access ───────────────────────────────────────────

export async function fetchUserSectionAccess(userId) {
  const { data, error } = await supabase
    .from("user_section_access")
    .select("department_id, is_visible")
    .eq("user_id", userId);
  if (error) throw error;
  return data || [];
}

// ── Favourites ───────────────────────────────────────────────

export async function fetchUserFavourites(userId, limit) {
  let q = supabase
    .from("user_favourites")
    .select("id, section_id, sections(id, name)")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (limit) q = q.limit(limit);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function deleteFavourite(userId, sectionId) {
  const { error } = await supabase
    .from("user_favourites")
    .delete()
    .eq("user_id", userId)
    .eq("section_id", sectionId);
  if (error) throw error;
}

export async function insertFavourite(userId, sectionId) {
  const { data, error } = await supabase
    .from("user_favourites")
    .insert({ user_id: userId, section_id: sectionId })
    .select("id, section_id, sections(id, name)")
    .single();
  if (error) throw error;
  return data;
}

// ── Activity ─────────────────────────────────────────────────

export async function insertUserActivity(fields) {
  const { error } = await supabase
    .from("user_activity")
    .insert(fields);
  if (error) throw error;
}

export async function purgeOldActivity(userId, keepCount) {
  const { data: rows } = await supabase
    .from("user_activity")
    .select("id")
    .eq("user_id", userId)
    .order("visited_at", { ascending: false });
  if (rows && rows.length > keepCount) {
    const old = rows.slice(keepCount).map((r) => r.id);
    await supabase.from("user_activity").delete().in("id", old);
  }
}

export async function fetchRecentActivity(userId, brandId) {
  const { data, error } = await supabase
    .from("user_activity")
    .select("id, section_id, section_name, visited_at")
    .eq("user_id", userId)
    .eq("brand_id", brandId)
    .order("visited_at", { ascending: false })
    .limit(5);
  if (error) throw error;
  return data || [];
}

export async function cleanupStaleInviteAndMigrateAccess(email, realUserId) {
  try {
    const { data: stale } = await supabase
      .from("users")
      .select("id")
      .ilike("email", email)
      .neq("id", realUserId)
      .is("last_active", null)
      .maybeSingle();

    if (!stale) return;

    await supabase
      .from("user_section_access")
      .update({ user_id: realUserId })
      .eq("user_id", stale.id);

    await supabase
      .from("users")
      .delete()
      .eq("id", stale.id);
  } catch (err) {
    console.error("[cleanupStaleInvite]", err.message);
  }
}
